import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Custom Hooks
import useSoketiRoom from '../hooks/useSoketiRoom';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';

// Components
import AuthGate from './room/AuthGate';
import RoomHeader from './room/RoomHeader';
import TranscriptFeed from './room/TranscriptFeed';
import WaveVisualizer from './room/WaveVisualizer';
import BillingButton from './room/BillingButton';
import VipButton from './room/VipButton';
import UserMenu from './room/UserMenu';

// Main Podcast Room Component
export default function PodcastRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const pb = window.pb;

  const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid);
  const [userId, setUserId] = useState(pb.authStore.record?.id || '');
  const [participantTag, setParticipantTag] = useState(pb.authStore.record?.name || 'Guest');

  // New UI States
  const [theme, setTheme] = useState('dark');
  const [showTranscript, setShowTranscript] = useState(false);

  const { playTTS, isAtlasSpeaking } = useSpeechSynthesis();
  const { transcript, roomMeta, setRoomMeta, isHost, roomRecordId } = useSoketiRoom(roomId, isAuthenticated, pb, playTTS);
  const { isListening, toggleListening } = useSpeechRecognition(roomId, participantTag, userId);

  const isDark = theme === 'dark';
  const colors = {
    bg: isDark ? '#121212' : '#f9f9f9',
    surface: isDark ? '#1e1e1e' : '#ffffff',
    text: isDark ? '#e0e0e0' : '#333333',
    border: isDark ? '#333' : '#e0e0e0',
    primary: isDark ? '#8a2be2' : '#0275d8'
  };

  // 1. Add these two new state lines for the VIP toggle
  const isVip = pb.authStore.record?.isVip || pb.authStore.record?.role === 'vip';
  const [useDeepSeek, setUseDeepSeek] = useState(false);

  // iOS WebKit Audio Unlock Hack
  const handleTapToSpeak = () => {
    // Only fire the unlock utterance if we are starting the session
    if (!isListening) {
      const unlockUtterance = new SpeechSynthesisUtterance('');
      window.speechSynthesis.speak(unlockUtterance);
    }

    // Proceed with your normal microphone toggle
    toggleListening();
  };

  if (!isAuthenticated) {
    return <AuthGate pb={pb} roomId={roomId} onSuccess={(r) => { setUserId(r.id); setParticipantTag(r.name); setIsAuthenticated(true); }} />;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg, color: colors.text, transition: 'all 0.3s ease' }}>

      {/* Top Navbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 30px', backgroundColor: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {/* <span style={{ fontSize: '12px', padding: '4px 8px', background: colors.primary, color: '#fff', borderRadius: '4px', fontWeight: 'bold' }}>
            ID: {roomId}
          </span> */}
          <button onClick={() => navigate('/archive')} style={{ background: 'transparent', color: colors.text, border: `1px solid ${colors.border}`, padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>
            Archives
          </button>
        </div>

        {/* 👇 Right-hand Controls (Billing + Theme Toggle) 👇 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* <BillingButton pb={pb} roomId={roomId} colors={colors} /> */}
          {isVip ? (
            <BillingButton pb={pb} roomId={roomId} colors={colors} />
          ) : (
            <button
              onClick={() => navigate(`/checkout?roomId=${roomId}`)}
              style={{ background: colors.primary, color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', border: 'none', fontWeight: 'bold' }}>
              Subscribe
            </button>
          )}
          <UserMenu pb={pb} setIsAuthenticated={setIsAuthenticated} colors={colors} participantTag={participantTag} />
          <button onClick={() => setTheme(isDark ? 'light' : 'dark')} style={{ background: 'transparent', color: colors.text, border: 'none', cursor: 'pointer', fontSize: '20px' }}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
      </div>


      <div style={{ padding: '40px 20px', maxWidth: '800px', margin: '0 auto' }}>

        {/* Room Meta (Header) */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <RoomHeader roomMeta={roomMeta} setRoomMeta={setRoomMeta} isHost={isHost} roomRecordId={roomRecordId} pb={pb} />
        </div>

        {/* Central Wave Animation */}
        <div style={{ backgroundColor: colors.surface, padding: '40px', borderRadius: '16px', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
          <WaveVisualizer isListening={isListening} isAtlasSpeaking={isAtlasSpeaking} theme={theme} />

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
            <button
              onClick={handleTapToSpeak}
              style={{
                background: isListening ? '#d9534f' : colors.primary,
                color: '#fff', padding: '15px 40px', fontSize: '18px', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: 'bold', transition: 'background 0.3s ease'
              }}>
              {isListening ? '🛑 Stop Recording' : '🎤 Tap to Speak'}
            </button>
          </div>
        </div>

        {/* Transcript Toggle */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            style={{ background: 'transparent', color: colors.text, border: `1px solid ${colors.border}`, padding: '10px 20px', borderRadius: '20px', cursor: 'pointer', fontSize: '14px' }}
          >
            {showTranscript ? 'Hide Transcript ▲' : 'View Live Transcript ▼'}
          </button>
        </div>

        {/* Collapsible Transcript */}
        {showTranscript && (
          <div style={{ animation: 'fadeIn 0.3s ease-in' }}>
            <TranscriptFeed transcript={transcript} theme={theme} colors={colors} />
          </div>
        )}

      </div>
    </div>
  );
}